"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { logger } from "@/lib/logger";
import getConfig from "@/app/utils/config";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Send, Sparkles, Mic, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  applyIncomingSupportMessage,
  cleanSupportMessageText,
  mergeChatHistory,
  supportMessageKey,
  type SupportChatMessage,
} from "@/app/lib/supportChatUtils";

const SUPPORT_ONBOARDING_KEY = "supportCentreOnboardingDone";

/** POST body: one of `email` | `userId` | `anonId` (same shape as other support-chat calls). Wire path on chat service. */
const CLEAR_PROMPT_MESSAGES_PATH = "/api/support-chat/clear-prompt-messages";

const AI_CHAT_AVATAR = "/images/customer-support/robo-cs.png";
const HUMAN_SUPPORT_AVATAR = "/support-admin.png";

const escalationSoftMotion = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] as const },
};

type Message = SupportChatMessage;

const EscalationBanner = React.memo(function EscalationBanner({
  label,
}: {
  label: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div className="w-full" {...escalationSoftMotion}>
      <style jsx>{`
        @keyframes support-banner-thinking {
          0% {
            background-position: 150% center;
          }
          100% {
            background-position: -50% center;
          }
        }
        .support-banner-radiant {
          display: block;
          width: 100%;
          direction: ltr;
          font-family: Inter, sans-serif;
          font-weight: 500;
          font-size: 13.5px;
          line-height: 1.6;
          letter-spacing: 0.02em;
          text-align: center;
          color: transparent;
          -webkit-text-fill-color: transparent;
          -webkit-background-clip: text;
          background-clip: text;
          background-image: linear-gradient(
            270deg,
            #767EF6 0%,
            #767EF6 30%,
            #949bf8 40%,
            #b1b6f9 46%,
            #c8ccfa 50%,
            #b1b6f9 54%,
            #949bf8 60%,
            #767EF6 70%,
            #767EF6 100%
          );
          background-size: 250% auto;
          animation: support-banner-thinking 2.8s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .support-banner-radiant {
            animation: none;
            background-image: none;
            -webkit-background-clip: unset;
            background-clip: unset;
            color: #767EF6;
            -webkit-text-fill-color: #767EF6;
          }
        }
      `}</style>
      <div className="w-full px-4 flex justify-center" role="status" aria-live="polite">
        <div className="relative group w-full max-w-full sm:max-w-lg">
            {/* Animated ring: continuous stroke-dash travel (Optimized 5-Layer SVG without Filters) */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
              aria-hidden
            >
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                className="fill-none stroke-white/10"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
              />
              {/* Glow Layer 1: Widest and faintest */}
              <motion.rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                pathLength={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
                className="fill-none"
                stroke="rgba(255,255,255,0.01)"
                strokeWidth="8"
                strokeDasharray="0.11 0.89"
                initial={{ strokeDashoffset: 0 }}
                animate={
                  reduceMotion
                    ? { strokeDashoffset: 0 }
                    : { strokeDashoffset: [0, -1] }
                }
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        duration: 5.5,
                        repeat: Infinity,
                        ease: "linear",
                        repeatType: "loop",
                      }
                }
              />
              {/* Glow Layer 2: Wide and faint */}
              <motion.rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                pathLength={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
                className="fill-none"
                stroke="rgba(255,255,255,0.03)"
                strokeWidth="5"
                strokeDasharray="0.11 0.89"
                initial={{ strokeDashoffset: 0 }}
                animate={
                  reduceMotion
                    ? { strokeDashoffset: 0 }
                    : { strokeDashoffset: [0, -1] }
                }
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        duration: 5.5,
                        repeat: Infinity,
                        ease: "linear",
                        repeatType: "loop",
                      }
                }
              />
              {/* Glow Layer 3: Medium and semi-transparent */}
              <motion.rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                pathLength={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
                className="fill-none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="3"
                strokeDasharray="0.11 0.89"
                initial={{ strokeDashoffset: 0 }}
                animate={
                  reduceMotion
                    ? { strokeDashoffset: 0 }
                    : { strokeDashoffset: [0, -1] }
                }
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        duration: 5.5,
                        repeat: Infinity,
                        ease: "linear",
                        repeatType: "loop",
                      }
                }
              />
              {/* Core Layer 1: Crisp leading edge */}
              <motion.rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                pathLength={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
                className="fill-none"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth="2"
                strokeDasharray="0.11 0.89"
                initial={{ strokeDashoffset: 0 }}
                animate={
                  reduceMotion
                    ? { strokeDashoffset: 0 }
                    : { strokeDashoffset: [0, -1] }
                }
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        duration: 5.5,
                        repeat: Infinity,
                        ease: "linear",
                        repeatType: "loop",
                      }
                }
              />
              {/* Core Layer 2: Intense bright center */}
              <motion.rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                pathLength={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
                className="fill-none"
                stroke="rgba(255,255,255,0.4)"
                strokeWidth="1"
                strokeDasharray="0.11 0.89"
                initial={{ strokeDashoffset: 0 }}
                animate={
                  reduceMotion
                    ? { strokeDashoffset: 0 }
                    : { strokeDashoffset: [0, -1] }
                }
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : {
                        duration: 5.5,
                        repeat: Infinity,
                        ease: "linear",
                        repeatType: "loop",
                      }
                }
              />
            </svg>

          {/* Actual Content Container */}
          <div className="bg-[#1a1a1a]/75 backdrop-blur-md border border-white/5 rounded-xl py-4 px-6 sm:px-8 text-center shadow-xl relative z-10">
            <p className="text-center relative z-[1]">
              <span className="support-banner-radiant">
                {label}
              </span>
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

interface EscalationPromptProps {
  title: string;
  keepChattingLabel: string;
  connectLabel: string;
  clearPromptMessagesOnServer: () => Promise<void>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  sendMessage: (textOverride?: string, forceVisible?: boolean) => Promise<boolean>;
}

const EscalationPrompt = React.memo(function EscalationPrompt({
  title,
  keepChattingLabel,
  connectLabel,
  clearPromptMessagesOnServer,
  setMessages,
  sendMessage,
}: EscalationPromptProps) {
  const [submittingAction, setSubmittingAction] = React.useState<"keepChatting" | "connect" | null>(null);

  const handleKeepChatting = React.useCallback(async () => {
    if (submittingAction) return;
    setSubmittingAction("keepChatting");
    setMessages((prev) => prev.filter((m) => m.type !== "prompt"));
    try {
      await clearPromptMessagesOnServer();
    } catch (cleanupError) {
      logger.error("Failed to clear prompt on server:", cleanupError instanceof Error ? cleanupError : new Error(String(cleanupError)), {
        context: "SupportPageClient.handleKeepChatting",
      });
    }
  }, [submittingAction, clearPromptMessagesOnServer, setMessages]);

  const handleConnect = React.useCallback(async () => {
    if (submittingAction) return;
    setSubmittingAction("connect");
    try {
      const success = await sendMessage("connect to support");
      if (success === false) throw new Error("Failed to send connect message");
      setMessages((prev) => prev.filter((m) => m.type !== "prompt"));
      try {
        await clearPromptMessagesOnServer();
      } catch (cleanupError) {
        logger.error("Failed to clear prompt on server:", cleanupError instanceof Error ? cleanupError : new Error(String(cleanupError)), {
          context: "SupportPageClient.handleConnect",
        });
      }
    } catch (error) {
      logger.error("Connect to support error:", error instanceof Error ? error : new Error(String(error)), {
        context: "SupportPageClient.handleConnect",
      });
      setSubmittingAction(null);
    }
  }, [submittingAction, clearPromptMessagesOnServer, setMessages, sendMessage]);

  const isSubmitting = submittingAction !== null;

  return (
    <motion.div
      className="w-full px-4 flex justify-center"
      {...escalationSoftMotion}
    >
      <div
        className="bg-[#1A1919]/80 backdrop-blur-sm border border-white/5 rounded-xl py-6 px-5 max-w-lg  w-full text-center shadow-lg"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p
          className="text-[#767EF6] mb-5"
          style={{
            fontWeight: 500,
            fontSize: "13.5px",
            lineHeight: "1.6",
            letterSpacing: "0.02em",
          }}
        >
          {title}
        </p>
        <div className="flex items-center justify-center gap-4">
          <button
            disabled={isSubmitting}
            onClick={handleKeepChatting}
            className={`px-5 py-2 flex items-center justify-center gap-2 rounded-lg border border-white/10 text-xs font-medium transition-all duration-200 ${isSubmitting ? "text-white/30 cursor-not-allowed" : "text-white/70 hover:bg-white/5 hover:text-white"}`}
          >
            {submittingAction === "keepChatting" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <span>{keepChattingLabel}</span>
          </button>
          <button
            disabled={isSubmitting}
            onClick={handleConnect}
            className={`px-5 py-2 flex items-center justify-center gap-2 rounded-lg border border-white/10 text-xs font-medium transition-all duration-200 ${isSubmitting ? "text-white/30 cursor-not-allowed" : "text-white/70 hover:bg-white/5 hover:text-white"}`}
          >
            {submittingAction === "connect" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <span>{connectLabel}</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
});

const ChatLoader = React.memo(function ChatLoader({
  fill,
}: {
  fill?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex flex-col items-center justify-center w-full bg-transparent py-8 sm:py-12 shrink-0 ${
        fill ? "min-h-[min(400px,50vh)]" : ""
      }`}
    >
      <div className="three-body">
        <div className="three-body__dot" />
        <div className="three-body__dot" />
        <div className="three-body__dot" />
      </div>
      <style jsx>{`
        .three-body {
          --uib-size: 35px;
          --uib-speed: 0.8s;
          --uib-color: #5d3fd3;
          position: relative;
          display: inline-block;
          height: var(--uib-size);
          width: var(--uib-size);
          animation: spin78236 calc(var(--uib-speed) * 2.5) infinite linear;
        }

        .three-body__dot {
          position: absolute;
          height: 100%;
          width: 30%;
        }

        .three-body__dot:after {
          content: "";
          position: absolute;
          height: 0%;
          width: 100%;
          padding-bottom: 100%;
          background-color: var(--uib-color);
          border-radius: 50%;
        }

        .three-body__dot:nth-child(1) {
          bottom: 5%;
          left: 0;
          transform: rotate(60deg);
          transform-origin: 50% 85%;
        }

        .three-body__dot:nth-child(1)::after {
          bottom: 0;
          left: 0;
          animation: wobble1 var(--uib-speed) infinite ease-in-out;
          animation-delay: calc(var(--uib-speed) * -0.3);
        }

        .three-body__dot:nth-child(2) {
          bottom: 5%;
          right: 0;
          transform: rotate(-60deg);
          transform-origin: 50% 85%;
        }

        .three-body__dot:nth-child(2)::after {
          bottom: 0;
          left: 0;
          animation: wobble1 var(--uib-speed) infinite
            calc(var(--uib-speed) * -0.15) ease-in-out;
        }

        .three-body__dot:nth-child(3) {
          bottom: -5%;
          left: 0;
          transform: translateX(116.666%);
        }

        .three-body__dot:nth-child(3)::after {
          top: 0;
          left: 0;
          animation: wobble2 var(--uib-speed) infinite ease-in-out;
        }

        @keyframes spin78236 {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes wobble1 {
          0%,
          100% {
            transform: translateY(0%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateY(-66%) scale(0.65);
            opacity: 0.8;
          }
        }

        @keyframes wobble2 {
          0%,
          100% {
            transform: translateY(0%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateY(66%) scale(0.65);
            opacity: 0.8;
          }
        }
      `}</style>
    </motion.div>
  );
});

const WelcomeMessage = React.memo(function WelcomeMessage({
  text,
}: {
  text: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-48 h-48 sm:w-64 sm:h-64 flex items-center justify-center">
        <img
          src="/images/chatRobot.gif"
          alt="Chat Robot"
          className="w-full h-full object-contain"
          width={256}
          height={256}
        />
      </div>
      <p className="text-center text-white text-sm sm:text-base font-medium px-4">
        {text}
      </p>
    </div>
  );
});

export default function SupportPageClient() {
  const t = useTranslations("SupportPage");
  const [mounted, setMounted] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [onboardingInput, setOnboardingInput] = useState("");

  // Chat state
  const [input, setInput] = useState("");
  const [userPackage, setUserPackage] = useState<string>("hobby");
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chatState, setChatState] = useState<"loading" | "new" | "existing">(
    "loading",
  );
  const [isTyping, setIsTyping] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [anonId, setAnonId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const committedTranscriptRef = useRef("");
  // const [showUserChoosenEscalation, setShowUserChoosenEscalation] =
  //   useState(false);
  // const [showDerectEscalation, setShowDerectEscalation] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [textareaHeight, setTextareaHeight] = useState<number>(32);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const shadowDivRef = useRef<HTMLDivElement | null>(null);
  const shadowWideDivRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (shadowDivRef.current?.parentNode) {
        shadowDivRef.current.parentNode.removeChild(shadowDivRef.current);
      }
      if (shadowWideDivRef.current?.parentNode) {
        shadowWideDivRef.current.parentNode.removeChild(shadowWideDivRef.current);
      }
    };
  }, []);

  const playSound = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!audioRef.current) {
      audioRef.current = new Audio("/sounds/chatSound.mp3");
      audioRef.current.volume = 0.35;
    }
    try {
      audioRef.current.currentTime = 0;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      audioRef.current.play();
    } catch {
      // Ignore sound errors (e.g. autoplay restrictions)
    }
  }, []);

  const scrollContainerToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const container = messagesContainerRef.current;
      if (!container) return;
      const top = Math.max(0, container.scrollHeight - container.clientHeight);
      if (behavior === "auto") {
        container.scrollTop = top;
      } else {
        container.scrollTo({ top, behavior });
      }
    },
    [],
  );

  // Scroll the messages panel only (not the page) after layout settles.
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollContainerToBottom(behavior);
          // Framer-motion + markdown can grow content after the first paint.
          window.setTimeout(() => scrollContainerToBottom("auto"), 150);
          window.setTimeout(() => scrollContainerToBottom("auto"), 400);
        });
      });
    },
    [scrollContainerToBottom],
  );

  const wasNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight <= 80;
  }, []);

  const handleMessagesScroll = useCallback(() => {
    nearBottomRef.current = wasNearBottom();
  }, [wasNearBottom]);

  // Read onboarding state from localStorage (client-only)
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {}
        recognitionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mounted || typeof window === "undefined") return;
    const done = localStorage.getItem(SUPPORT_ONBOARDING_KEY) === "true";
    setShowOnboarding(!done);
  }, [mounted]);

  // User identity
  useEffect(() => {
    if (!mounted) return;
    let anon = localStorage.getItem("anonChatId");
    if (!anon) {
      // Some browser runtimes have `crypto`, but not `crypto.randomUUID()`.
      // Use a feature check + fallback so support chat never crashes.
      const maybeRandomUUID = (globalThis as any)?.crypto?.randomUUID;
      anon =
        typeof maybeRandomUUID === "function"
          ? maybeRandomUUID.call((globalThis as any).crypto)
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("anonChatId", anon);
    }

    const fetchUser = async () => {
      try {
        const res = await fetch("/api/auth/user", { method: "GET" });
        if (res.ok) {
          const data = await res.json();
          setUserEmail(data.user?.email || null);
          setUserId(data.user?.id || null);
          if (data.user?.id) localStorage.setItem("chatUserId", data.user.id);
          // Any verified identity (id or email) should drop the anon id so chat
          // history stays under one identity instead of fragmenting.
          if (data.user?.id || data.user?.email) {
            localStorage.removeItem("anonChatId");
            setAnonId(null);
          } else {
            setAnonId(anon);
          }
        } else {
          setAnonId(anon);
        }
      } catch {
        setAnonId(anon);
      }
    };
    fetchUser();
  }, [mounted]);

  // Socket
  useEffect(() => {
    const url = getConfig().SUPPORT_CHAT_URL;
    if (!url) return;
    const socket = io(url, {
      path: "/socket.io/",
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;
    socket.on("connect", () => setIsConnected(true));
    socket.on("disconnect", () => setIsConnected(false));
    socket.on("connect_error", () => setIsConnected(false));
    return () => {
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, []);

  const room = userEmail || userId || anonId;
  useEffect(() => {
    if (socketRef.current?.connected && room) {
      socketRef.current.emit("join-chat", room);
    }
  }, [room, isConnected]);

  // Load history when we have identity
  useEffect(() => {
    if (!room) return;

    setChatState("loading");
    const chatServiceUrl = getConfig().SUPPORT_CHAT_URL;
    const params = new URLSearchParams();
    if (userEmail) params.append("email", userEmail);
    else if (userId) params.append("userId", userId);
    else if (anonId) params.append("anonId", anonId);
    const fetchUrl = `${chatServiceUrl}/api/support-chat?${params.toString()}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      fetch(fetchUrl, { signal: controller.signal })
        .then(async (res) => {
          if (!res.ok) return { messages: [] };
          const ct = res.headers.get("content-type");
          if (!ct?.includes("application/json")) return { messages: [] };
          return res.json();
        })
        .then((data) => {
          const loaded = data.messages || [];
          if (loaded.length > 0) {
            setShowOnboarding(false);
            if (typeof window !== "undefined") {
              localStorage.setItem(SUPPORT_ONBOARDING_KEY, "true");
            }
          } else {
            setShowOnboarding(true);
          }
          setMessages((prev) => {
            if (loaded.length > 0) {
              setChatState("existing");
              return mergeChatHistory(loaded, prev);
            }
            if (prev.length > 0) {
              setChatState("existing");
              return prev;
            }
            setChatState("new");
            return [];
          });
          if (loaded.length > 0) {
            scrollToBottom("auto");
          }
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setMessages([]);
          setChatState("new");
          setShowOnboarding(true);
        });
    }, 300);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [room, userEmail, userId, anonId, scrollToBottom]);

  // User package
  useEffect(() => {
    if (!userEmail) return;
    fetch(`/api/user-packages?email=${encodeURIComponent(userEmail)}`)
      .then((r) => (r.ok ? r.json() : { package: "hobby" }))
      .then((d) => d.package && setUserPackage(d.package))
      .catch(() => {});
  }, [userEmail]);

  // Real-time new messages
  useEffect(() => {
    if (!socketRef.current || showOnboarding) return;
    const socket = socketRef.current;
    const handler = (data: { chatId: string; message: Message }) => {
      const msg = data.message;
      if (msg.from === "user") return;
      setLoading(false);

      setIsTyping(false);
      setMessages((prev) => applyIncomingSupportMessage(prev, msg));
      scrollToBottom("smooth");
      playSound();
    };

    const handleMessageDeleted = (data: {
      chatId: string;
      messageId: string;
    }) => {
      setMessages((prev) => prev.filter((m) => m._id !== data.messageId));
    };

    const handleMessageUpdated = (data: {
      chatId: string;
      message: Message;
    }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m._id === data.message._id ? { ...m, text: data.message.text } : m,
        ),
      );
    };

    const handleTyping = (data: { isTyping: boolean }) => {
      setIsTyping(data.isTyping);
    };

    socket.on("new-message", handler);
    socket.on("message-deleted", handleMessageDeleted);
    socket.on("message-updated", handleMessageUpdated);
    socket.on("typing", handleTyping);

    return () => {
      // Pass the same handler ref so we only remove this effect's listener.
      socket.off("new-message", handler);
      socket.off("message-deleted", handleMessageDeleted);
      socket.off("message-updated", handleMessageUpdated);
      socket.off("typing", handleTyping);
    };
  }, [showOnboarding, scrollToBottom, playSound]);

  const sendMessage = useCallback(
    async (textOverride?: string, forceVisible = false): Promise<boolean> => {
      const messageText = (textOverride ?? input).trim();
      if (!messageText || loading) return false;

      if (isListening && recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
        committedTranscriptRef.current = "";
        setIsListening(false);
      }

      setInput("");
      setIsMultiline(false);
      setTextareaHeight(32);
      setLoading(true);

      const tempMessage: Message = {
        from: "user",
        text: messageText,
        createdAt: new Date().toISOString(),
        type: "message",
        visibility: forceVisible ? true : textOverride ? false : true,
        isDeleted: false,
        temp: true,
      };
      nearBottomRef.current = true;
      setMessages((prev) => [...prev, tempMessage]);
      scrollToBottom("smooth");
      // setIsTyping(true);
      playSound();

      try {
        const chatServiceUrl = getConfig().SUPPORT_CHAT_URL;
        const body: Record<string, unknown> = {
          text: messageText,
          from: "user",
          userType: userEmail || userId ? "verified" : "anonymous",
          userPackage: userPackage,
          type: "message",
          visibility: forceVisible ? true : textOverride ? false : true,
        };
        if (userEmail) body.email = userEmail;
        if (userId) body.userId = userId;
        else if (anonId) body.anonId = anonId;

        const response = await fetch(`${chatServiceUrl}/api/support-chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error(`Send failed: ${response.status}`);
        await response.json();

        setMessages((prev) =>
          prev.map((msg) =>
            msg.temp && msg.text === messageText
              ? { ...msg, temp: false }
              : msg,
          ),
        );
        setChatState("existing");
        return true;
      } catch (err) {
        setIsTyping(false);
        logger.error("Support chat send failed", err, {
          context: "SupportPageClient.sendMessage",
        });
        setError(t("errorSend"));
        setTimeout(() => setError(""), 3000);
        setMessages((prev) => prev.filter((m) => !m.temp));
        return false;
      } finally {
        setLoading(false);
        scrollToBottom("smooth");
      }
    },
    [
      input,
      loading,
      userEmail,
      userId,
      anonId,
      userPackage,
      t,
      playSound,
      isListening,
      scrollToBottom,
    ],
  );

  // Scroll to end on a new message or typing change (not on every keystroke).
  useEffect(() => {
    if (nearBottomRef.current) {
      scrollToBottom();
    }
  }, [messages.length, isTyping, scrollToBottom]);

  // Keep pinned to bottom while content height changes (markdown, animations).
  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesContentRef.current;
    if (!container || !content) return;

    const observer = new ResizeObserver(() => {
      if (!nearBottomRef.current) return;
      scrollContainerToBottom("auto");
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [scrollContainerToBottom, showOnboarding, chatState]);

  const handleDeleteChat = async () => {
    if (!userEmail && !userId && !anonId) return;

    setLoading(true);
    try {
      const chatServiceUrl = getConfig().SUPPORT_CHAT_URL;

      const requestBody: Record<string, string> = {};
      if (userEmail) requestBody.email = userEmail;
      else if (userId) requestBody.userId = userId;
      else if (anonId) requestBody.anonId = anonId;

      const response = await fetch(`${chatServiceUrl}/api/support-chat`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete chat: ${response.status}`);
      }

      setMessages([]);
      setChatState("new");
      setShowOnboarding(true);
    } catch (err) {
      logger.error(
        "Support chat: failed to delete chat",
        err instanceof Error ? err : new Error(String(err)),
        { context: "SupportPageClient.deleteChat" },
      );
      setError(t("errorSend"));
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const clearPromptMessagesOnServer = useCallback(async () => {
    const chatServiceUrl = getConfig().SUPPORT_CHAT_URL;
    const body: Record<string, string> = {};
    if (userEmail) body.email = userEmail;
    else if (userId) body.userId = userId;
    else if (anonId) body.anonId = anonId;

    if (!chatServiceUrl || Object.keys(body).length === 0) return;

    try {
      const res = await fetch(
        `${chatServiceUrl}${CLEAR_PROMPT_MESSAGES_PATH}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        logger.warn("Clear prompt messages returned non-OK status", {
          status: res.status,
          context: "SupportPageClient.clearPromptMessages",
        });
      }
    } catch (err) {
      logger.error(
        "Clear prompt messages request failed",
        err instanceof Error ? err : new Error(String(err)),
        { context: "SupportPageClient.clearPromptMessages" },
      );
    }
  }, [userEmail, userId, anonId]);

  // const clearBannerMessagesOnServer = useCallback(async () => {
  //   const chatServiceUrl = getConfig().SUPPORT_CHAT_URL;
  //   const body: Record<string, string> = {};
  //   if (userEmail) body.email = userEmail;
  //   else if (userId) body.userId = userId;
  //   else if (anonId) body.anonId = anonId;

  //   if (!chatServiceUrl || Object.keys(body).length === 0) return;

  //   try {
  //     const res = await fetch(`${chatServiceUrl}${CLEAR_BANNER_MESSAGES_PATH}`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(body),
  //     });
  //     if (!res.ok) {
  //       logger.warn("Clear banner messages returned non-OK status", {
  //         status: res.status,
  //         context: "SupportPageClient.clearBannerMessages",
  //       });
  //     }
  //   } catch (err) {
  //     logger.error(
  //       "Clear banner messages request failed",
  //       err instanceof Error ? err : new Error(String(err)),
  //       { context: "SupportPageClient.clearBannerMessages" },
  //     );
  //   }
  // }, [userEmail, userId, anonId]);

  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Narrow width measurement to prevent infinite expand/collapse loop
    const container = textarea.closest('.max-w-3xl');
    const containerWidth = container ? container.clientWidth : textarea.offsetWidth + 160;
    const narrowWidth = Math.max(containerWidth - 160, 100);

    let tempDiv = shadowDivRef.current;
    if (!tempDiv) {
      tempDiv = document.createElement("div");
      document.body.appendChild(tempDiv);
      shadowDivRef.current = tempDiv;
    }

    const computedStyle = window.getComputedStyle(textarea);
    
    tempDiv.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre-wrap;
      word-wrap: break-word;
      width: ${narrowWidth}px;
      font-family: ${computedStyle.fontFamily};
      font-size: ${computedStyle.fontSize};
      font-weight: ${computedStyle.fontWeight};
      line-height: ${computedStyle.lineHeight};
      padding: ${computedStyle.paddingTop} ${computedStyle.paddingRight} ${computedStyle.paddingBottom} ${computedStyle.paddingLeft};
      box-sizing: border-box;
    `;
    tempDiv.textContent = textarea.value || " ";
    
    const textHeight = tempDiv.offsetHeight;

    const isMulti = textHeight > 36 || textarea.value.includes('\n');
    setIsMultiline(isMulti);

    // Calculate height based on actual rendered width to ensure correct visual height
    let wideDiv = shadowWideDivRef.current;
    if (!wideDiv) {
      wideDiv = document.createElement("div");
      document.body.appendChild(wideDiv);
      shadowWideDivRef.current = wideDiv;
    }
    wideDiv.style.cssText = tempDiv.style.cssText;
    wideDiv.style.width = `${textarea.offsetWidth}px`;
    wideDiv.textContent = textarea.value || " ";
    
    const actualHeight = wideDiv.offsetHeight;

    const maxHeight = window.innerWidth < 640 ? 100 : 120;
    setTextareaHeight(Math.max(32, Math.min(actualHeight, maxHeight)));
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [input]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === "undefined") return;

    let previousWidth = textarea.clientWidth;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Only trigger autoResizeTextarea if the width has actually changed,
        // to avoid triggering resizing layout updates from height changes.
        const width = entry.contentRect.width;
        if (width !== previousWidth) {
          previousWidth = width;
          autoResizeTextarea();
        }
      }
    });

    observer.observe(textarea);
    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleMic = useCallback(() => {
    const win = typeof window !== "undefined" ? window : null;
    const SpeechRecognition =
      win &&
      ((win as any).SpeechRecognition || (win as any).webkitSpeechRecognition);

    if (!SpeechRecognition) {
      setError("Speech input is not supported in this browser.");
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      committedTranscriptRef.current = "";
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || "en-US";

    recognition.onresult = (event: any) => {
      let finalTranscript = "";
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      if (finalTranscript) {
        committedTranscriptRef.current += finalTranscript + " ";
      }
      const toShow = committedTranscriptRef.current + interimTranscript;
      setInput(toShow.trimEnd());
      setTimeout(() => autoResizeTextarea(), 0);
      if (
        chatState === "new" &&
        (committedTranscriptRef.current || interimTranscript)
      ) {
        setChatState("existing");
      }
    };

    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        try {
          recognition.start();
        } catch {
          recognitionRef.current = null;
          setIsListening(false);
        }
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (event: any) => {
      if (event.error !== "aborted" && event.error !== "no-speech") {
        setIsListening(false);
        recognitionRef.current = null;
      }
    };

    committedTranscriptRef.current = input ? input + " " : "";
    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
    } catch (err) {
      setIsListening(false);
      setError("Could not start microphone.");
      setTimeout(() => setError(""), 3000);
    }
  }, [isListening, input, chatState, autoResizeTextarea]);

  const startSupportChat = (initialMessage?: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(SUPPORT_ONBOARDING_KEY, "true");
    }
    setShowOnboarding(false);
    if (initialMessage?.trim()) {
      sendMessage(initialMessage.trim(), true);
    }
  };

  const handleOnboardingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = onboardingInput.trim();
    startSupportChat(text || undefined);
    if (text) setOnboardingInput("");
  };

  if (!mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#191919]">
        <div className="three-body">
          <div className="three-body__dot" />
          <div className="three-body__dot" />
          <div className="three-body__dot" />
        </div>
        <style jsx>{`
          .three-body {
            --uib-size: 35px;
            --uib-speed: 0.8s;
            --uib-color: #5d3fd3;
            position: relative;
            display: inline-block;
            height: var(--uib-size);
            width: var(--uib-size);
          }
          .three-body__dot {
            position: absolute;
            height: 100%;
            width: 30%;
          }
          .three-body__dot:after {
            content: "";
            position: absolute;
            height: 0%;
            width: 100%;
            padding-bottom: 100%;
            background-color: var(--uib-color);
            border-radius: 50%;
          }
        `}</style>
      </div>
    );
  }

  // Full-page chat
  const chatBubbleStyles =
    ".chat-bubble-admin{background:#2D2C2C;padding:0.85rem 1.1rem;border-radius:0px 13px 13px 13px;max-width:88%;min-width:40px;width:fit-content;border:1px solid rgba(255,255,255,0.06);box-shadow:0 4px 15px rgba(0,0,0,0.1);font-size:13.5px;line-height:1.55;color:#e5e7eb;letter-spacing:0.01em;overflow-wrap:break-word;word-break:normal;white-space:pre-wrap;box-sizing:border-box;}" +
    ".chat-bubble-user{ background:radial-gradient(circle at 100% 100%, #6773F4 0%, #3541ED 100%);padding:0.8rem 1.1rem;border-radius:13px 13px 13px 13px;max-width:70%;min-width:40px;width:fit-content;box-shadow:0 4px 15px rgba(53, 65, 237, 0.15);font-size:13.5px;line-height:1.55;color:white;letter-spacing:0.01em;overflow-wrap:break-word;word-break:normal;white-space:pre-wrap;box-sizing:border-box;}" +
    ".chat-bubble-typing{background:#2D2C2C;padding:0.85rem 1.1rem;border-radius:0px 13px 13px 13px;display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,0.06)}" +
    ".support-ui-scrollbar{scrollbar-width:thin;scrollbar-color:#2b2b2b transparent;}" +
    ".support-ui-scrollbar::-webkit-scrollbar{width:3px;height:3px;}" +
    ".support-ui-scrollbar::-webkit-scrollbar-track{background:transparent;}" +
    ".support-ui-scrollbar::-webkit-scrollbar-thumb{background-color:#2a2a2a;border-radius:9999px;border:none;}" +
    ".support-ui-scrollbar::-webkit-scrollbar-thumb:hover{background-color:#2a2a2a;border:none;}";

  // Onboarding state (once per user)
  if (showOnboarding) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-4"
        style={{ backgroundColor: "#191919" }}
      >
        {chatState === "loading" ? (
          <ChatLoader />
        ) : (
          <>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white pb-20 sm:mb-10 text-center">
              {t("heroTitle")}
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-lg rounded-2xl p-6 sm:p-8 flex flex-col items-center gap-6"
              style={{ backgroundColor: "#232325" }}
            >
              <h2 className="text-[40px]  font-bold text-[#767EF6] text-center">
                {t("heroSubtitle")}
              </h2>
              <p className="text-[#FCFAFA] text-lg text-center">
                {t("heroDescription")}
              </p>
              <form
                onSubmit={handleOnboardingSubmit}
                className="w-full flex flex-col gap-4"
              >
                <input
                  type="text"
                  value={onboardingInput}
                  onChange={(e) => setOnboardingInput(e.target.value)}
                  placeholder={t("heroInputPlaceholder")}
                  className="w-full px-4 py-3 rounded-xl bg-[#1f1f1f] border border-white/10 text-white placeholder:text-white/50 outline-none focus:border-[#3541ED] transition-colors"
                />
                <button
                  type="submit"
                  className="w-full max-w-[200px] mx-auto block py-3 rounded-full text-white font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "#3541ED" }}
                >
                  {t("heroButton")}
                </button>
              </form>
            </motion.div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: chatBubbleStyles }} />
      <div className="flex flex-col h-[calc(100vh+50px)] min-h-0 bg-[#191919] pb-4">
        {/* Header */}
        <div className="flex-shrink-0 h-14 sm:h-16 w-full relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#3541ED] to-[#767EF6]" />
          <div className="relative h-full flex items-center justify-between px-4 sm:px-6">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img
                src={AI_CHAT_AVATAR}
                alt=""
                className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 object-contain"
                width={40}
                height={40}
              />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-white text-sm sm:text-base leading-tight">
                  PineGen AI
                </div>
                <div className="text-white/80 text-xs leading-tight">
                  {isConnected ? "AI Support Agent" : "Connecting..."}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Centered Black Container: Messages + Input */}
        <div className="flex-1 min-h-0 overflow-hidden pt-8 pb-8">
          <div className="w-full mx-auto h-full min-h-0">
            <div className="bg-[#0a0909] rounded-xl flex flex-col w-full max-w-[1350px] mx-auto h-full overflow-hidden ring-white/10 ring-1">
              {/* Scrollable Messages Area */}
              <div
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 sm:px-12 py-8 support-ui-scrollbar overscroll-contain"
                style={{
                  WebkitOverflowScrolling: "touch",
                  touchAction: "pan-y",
                }}
              >
                <div ref={messagesContentRef} className="min-h-0">
                  <AnimatePresence mode="wait">
                    {chatState === "loading" && (
                      <ChatLoader key="loading" fill />
                    )}
                    {chatState === "new" && messages.length === 0 && (
                      <WelcomeMessage key="new-chat" text={t("welcomeText")} />
                    )}
                    {(chatState === "existing" || messages.length > 0) && (
                      <motion.div
                        key="existing-chat"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-12 max-w-3xl mx-auto"
                      >
                        {messages.map((msg, i) => {
                          if (msg.visibility === false) return null;

                          // ── banner type ──
                          if (msg.type === "banner") {
                            return (
                              <motion.div
                                key={supportMessageKey(msg, i)}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.12 }}
                              >
                                <EscalationBanner
                                  label={t("escalationBanner")}
                                />
                              </motion.div>
                            );
                          }

                          // ── prompt type ──
                          if (msg.type === "prompt") {
                            return (
                              <motion.div
                                key={supportMessageKey(msg, i)}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.12 }}
                              >
                                <EscalationPrompt
                                  title={t("escalationPrompt")}
                                  keepChattingLabel={t(
                                    "escalationKeepChatting",
                                  )}
                                  connectLabel={t("escalationConnect")}
                                  clearPromptMessagesOnServer={
                                    clearPromptMessagesOnServer
                                  }
                                  setMessages={setMessages}
                                  sendMessage={sendMessage}
                                />
                              </motion.div>
                            );
                          }

                          // ── aiToAdmin: hidden from user view ──
                          if (msg.from === "aiToAdmin") return null;

                          // ── user message ──
                          if (msg.from === "user") {
                            return (
                              <motion.div
                                key={supportMessageKey(msg, i)}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.12 }}
                                className="flex justify-end"
                              >
                                <div className="flex flex-col items-end w-full">
                                  <div
                                    className={`chat-bubble-user ${msg.temp ? "opacity-70" : ""} `}
                                  >
                                    {cleanSupportMessageText(msg.text ?? "")}
                                  </div>
                                </div>
                              </motion.div>
                            );
                          }

                          // ── ai message ──
                          if (msg.from === "ai") {
                            return (
                              <motion.div
                                key={supportMessageKey(msg, i)}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.12 }}
                                className="flex justify-start"
                              >
                                <div className="flex items-start gap-2.5 w-full max-w-[85%]">
                                  <div className="w-9 h-9 rounded-full bg-[#1e1e1e] border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md">
                                    <img
                                      src={AI_CHAT_AVATAR}
                                      alt="AI"
                                      className="w-full h-full object-cover"
                                      width={36}
                                      height={36}
                                    />
                                  </div>
                                  <div className="flex flex-col items-start min-w-0 flex-1 relative">
                                    <span className="text-[10px] font-medium text-[#C8CAF4] absolute -top-5 left-0">
                                      AI Chat Bot
                                    </span>
                                    <div
                                      className={`chat-bubble-admin ${msg.temp ? "opacity-70" : ""}`}
                                    >
                                      <div className="whitespace-normal space-y-3 overflow-x-auto">
                                        <Markdown
                                          remarkPlugins={[remarkGfm]}
                                          components={{
                                            ul: ({ node, ...props }) => (
                                              <ul
                                                className="my-0 list-outside pl-5 [list-style-type:disc]"
                                                {...props}
                                              />
                                            ),
                                            ol: ({ node, ...props }) => (
                                              <ol
                                                className="my-0 list-outside pl-5 [list-style-type:decimal]"
                                                {...props}
                                              />
                                            ),
                                            li: ({ node, ...props }) => (
                                              <li
                                                className="my-2 py-0 leading-snug [&>p]:my-0"
                                                {...props}
                                              />
                                            ),
                                          }}
                                        >
                                          {cleanSupportMessageText(
                                            msg.text ?? "",
                                          )}
                                        </Markdown>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          }

                          // ── admin message ──
                          if (msg.from === "admin") {
                            return (
                              <motion.div
                                key={supportMessageKey(msg, i)}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.12 }}
                                className="flex mb-3 justify-start"
                              >
                                <div className="flex items-start gap-2.5 w-full max-w-[85%]">
                                  <div className="w-9 h-9 rounded-full bg-[#2E2F3A] border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md">
                                    <img
                                      src={HUMAN_SUPPORT_AVATAR}
                                      alt="Support"
                                      className="w-full h-full object-cover object-top"
                                      width={36}
                                      height={36}
                                    />
                                  </div>
                                  <div className="flex flex-col items-start min-w-0 flex-1 relative">
                                    <span className="text-[10px] font-medium text-[#C8CAF4] absolute -top-5 left-0">
                                      Support Team
                                    </span>
                                    <div
                                      className={`chat-bubble-admin ${msg.temp ? "opacity-70" : ""}`}
                                    >
                                      {cleanSupportMessageText(msg.text ?? "")}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          }

                          return null;
                        })}

                        {isTyping && (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex mb-3 justify-start"
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="w-9 h-9 rounded-full bg-[#1e1e1e] border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md mt-5">
                                <img
                                  src={AI_CHAT_AVATAR}
                                  alt="AI"
                                  className="w-full h-full object-cover"
                                  width={36}
                                  height={36}
                                />
                              </div>
                              <div className="flex flex-col items-start">
                                <span className="text-[10px] text-[#C8CAF4] font-medium mb-1 ml-0.5">
                                  AI Chat Bot
                                </span>
                                <div className="chat-bubble-typing">
                                  <span className="flex gap-1.5">
                                    <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                    <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                    <span className="w-1.5 h-1.5 bg-white/50 rounded-full animate-bounce"></span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div
                    ref={messagesEndRef}
                    aria-hidden
                    className="h-px w-full shrink-0"
                  />
                </div>
              </div>

              {/* Pinned Input Footer */}

              <div className="flex-shrink-0 px-6 sm:px-12 pb-5 bg-[#0a0909] ">
                <div className="h-[1px] border-t border-white/10 border-1 pt-5 max-w-3xl mx-auto "></div>
                <div className="max-w-3xl mx-auto mb-4">
                  <motion.div
                    initial={false}
                    animate={{ borderRadius: isMultiline ? 24 : 50 }}
                    transition={{
                      duration: 0.2,
                      ease: "easeOut",
                    }}
                    className={`bg-[#1f1f1f] border ${loading ? "border-[#3541ED]/30" : "border-[#3541ED]/80"} flex flex-wrap pl-6 pr-5 py-3 items-end ${
                      isMultiline ? "gap-y-3 justify-between" : "gap-3"
                    }`}
                  >
                    {/* Sparkles Icon */}
                    <motion.div
                      layout="position"
                      className={`flex items-center h-8 flex-shrink-0 ${isMultiline ? "order-2" : "order-1"}`}
                    >
                      <Sparkles
                        color="#3541ED"
                        size={18}
                        className="text-[#C5C7F2]"
                        strokeWidth={1.75}
                      />
                    </motion.div>

                    {/* Textarea - No layout prop so width snaps instantly without text wobble */}
                    <textarea
                      ref={textareaRef}
                      placeholder={t("inputPlaceholder")}
                      className={`outline-none bg-transparent resize-none overflow-y-auto text-sm text-white placeholder:text-white/50 placeholder:font-semibold whitespace-pre-wrap leading-5 py-1.5 support-ui-scrollbar ${loading ? "cursor-not-allowed" : ""} ${
                        isMultiline ? "order-1 w-full" : "order-2 flex-1"
                      }`}
                      style={{
                        height: `${textareaHeight}px`,
                        minHeight: "32px",
                        maxHeight: "120px",
                      }}
                      rows={1}
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value);
                        autoResizeTextarea();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (!loading) sendMessage();
                        }
                      }}
                      disabled={loading}
                    />

                    {/* Mic and Send Icons */}
                    <motion.div
                      layout="position"
                      className={`flex items-center gap-1 h-8 flex-shrink-0 ${isMultiline ? "order-3" : "order-3 ml-2"}`}
                    >
                      <button
                        type="button"
                        onClick={toggleMic}
                        className={`p-2 transition-colors duration-200 rounded-full flex items-center justify-center ${
                          isListening
                            ? "text-red-500 bg-red-500/10 animate-pulse"
                            : "text-white hover:bg-white/10"
                        }`}
                        aria-label={
                          isListening ? "Stop listening" : "Start listening"
                        }
                      >
                        <Mic size={20} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => sendMessage()}
                        disabled={loading || !input.trim()}
                        className="p-2 transition-colors rounded-full flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-50"
                        aria-label="Send message"
                      >
                        <Send size={20} strokeWidth={1.75} color="white" />
                      </button>
                    </motion.div>
                  </motion.div>
                  {error && (
                    <div className="max-w-3xl mx-auto mt-2 text-red-400 text-center text-sm">
                      {error}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
